/**
 * @owner @Appboy/intelligence
 *
 * Shared "optimizing on Click needs a link" logic, used by the Knockout VM and the React validators
 * so both paths agree. Callers pass a plain MessageBodyLinkInput extracted from their own message
 * representation; the decision logic lives here.
 */

import { isLinkToken, tokenizeOnNonLiquidWhitespace } from "@lib/smsLinkShorteningHelper/smsLinkShorteningHelper";

import { getEmailLinks } from "./getEmailLinks";
import type {
  ComponentStoreData,
  ContentOptimizationChannel,
  ContentOptimizationEmailLink,
  ContentOptimizationErrors,
} from "./types";

/** Pre-extracted message-body inputs; callers populate the field(s) for their channel. */
export interface MessageBodyLinkInput {
  channel: ContentOptimizationChannel | null;
  emailLinks?: ContentOptimizationEmailLink[] | null;
  emailBody?: string | null;
  smsTexts?: string[];
}

export type ClickOptimizationLinkIssue = "missing-link" | "all-links-excluded";

type LinkSource = string[][];

const getHttpAnchorHrefs = (html: string): string[] => {
  const document = new DOMParser().parseFromString(html, "text/html");
  return Array.from(document.querySelectorAll("a[href]"))
    .map((anchor) => anchor.getAttribute("href") ?? "")
    .filter((href) => /^https?:\/\//i.test(href));
};

const getTextLinks = (text: string): string[] => tokenizeOnNonLiquidWhitespace(text).filter(isLinkToken);

/**
 * Collects all links from the body and components once, so we can query them.
 */
class ClickableLinkAnalysis {
  private readonly channel: ContentOptimizationChannel | null;
  private readonly linkSources: LinkSource[];

  constructor(body: MessageBodyLinkInput, store: ComponentStoreData) {
    this.channel = body.channel;
    // The body is a link source, and each component is a link source
    this.linkSources =
      this.channel === "email" || this.channel === "sms"
        ? [[this.getBodyLinks(body)], ...this.getLinksByComponent(store)]
        : [];
  }

  /** True when every send is guaranteed a link: the body has one, or a component has a link in all its values. */
  hasGuaranteedLink(): boolean {
    return this.linkSources.some(
      (linksByValue) => linksByValue.length > 0 && linksByValue.every((links) => links.length > 0)
    );
  }

  // Every send is guaranteed a non-excluded link
  hasGuaranteedNonExcludedLink(excludedLinkPrefixes: readonly string[]): boolean {
    if (this.channel !== "email" && this.channel !== "sms") {
      return this.hasGuaranteedLink();
    }

    return this.linkSources.some(
      (linksByValue) =>
        linksByValue.length > 0 &&
        linksByValue.every((links) =>
          links.some((link) => !excludedLinkPrefixes.some((prefix) => link.startsWith(prefix)))
        )
    );
  }

  private getBodyLinks({ channel, emailLinks, emailBody, smsTexts }: MessageBodyLinkInput): string[] {
    if (channel === "email") {
      return getEmailLinks(emailLinks ?? null, emailBody ?? null).map((link) => link.url);
    }
    if (channel === "sms") {
      return (smsTexts ?? []).flatMap(getTextLinks);
    }
    return [];
  }

  private getLinksByComponent(store: ComponentStoreData): LinkSource[] {
    const deactivated = store.deactivatedValues ?? {};
    return Object.entries(store.components ?? {}).map(([name, values]) =>
      values
        .filter((value) => value && value.trim() !== "" && !(deactivated[name] ?? []).includes(value))
        .map((value) => this.getValueLinks(value))
    );
  }

  // Component values may not have 'lid' (aka link-aliased), so email values count any HTTP anchor.
  private getValueLinks(value: string): string[] {
    return this.channel === "email" ? [...getHttpAnchorHrefs(value), ...getTextLinks(value)] : getTextLinks(value);
  }
}

// Returns the link issue for an otherwise valid Click-optimized step, or undefined when no issue applies
export const getClickOptimizationLinkIssue = (
  requireClickableLink: boolean,
  errors: ContentOptimizationErrors,
  body: MessageBodyLinkInput,
  store: ComponentStoreData,
  excludedLinkPrefixes: readonly string[] = []
): ClickOptimizationLinkIssue | undefined => {
  if (!requireClickableLink) {
    return;
  }
  const stepOtherwiseValid = errors.overallComponentInvalidStatus == null && !errors.isMessageIncomplete;
  if (!stepOtherwiseValid) {
    return;
  }

  const linkAnalysis = new ClickableLinkAnalysis(body, store);
  if (!linkAnalysis.hasGuaranteedLink()) {
    return "missing-link";
  }

  const normalizedPrefixes = excludedLinkPrefixes.map((prefix) => prefix.trim()).filter(Boolean);
  if (!linkAnalysis.hasGuaranteedNonExcludedLink(normalizedPrefixes)) {
    return "all-links-excluded";
  }

  return;
};
